import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  buildProfileSeo,
  buildProfileSummary,
  getBusinessProfile,
  profileCompleteness,
  setBusinessProfilePublic,
  SOCIAL_NETWORKS,
  upsertBusinessProfile,
  WEEK_DAYS,
  type BusinessHour,
  type CoreBusinessProfile,
} from '@/lib/coreBusinessProfile';
import { verificationLevelMeta } from '@/lib/coreVerification';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

const TABS = [
  { key: 'infos', label: 'Informations', icon: 'ri-information-line' },
  { key: 'hours', label: 'Horaires', icon: 'ri-time-line' },
  { key: 'contact', label: 'Contact & réseaux', icon: 'ri-contacts-line' },
  { key: 'seo', label: 'SEO & aperçu', icon: 'ri-search-eye-line' },
] as const;

type TabKey = typeof TABS[number]['key'];

export default function BusinessProfileTab({ org }: Props) {
  const [profile, setProfile] = useState<CoreBusinessProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<TabKey>('infos');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [form, setForm] = useState({
    name: org.commercial_name || '', description: '', shortDescription: '', category: '', logo: '', cover: '',
    address: '', city: '', country: org.country || '', phone: org.phone || '', whatsapp: '', email: org.email || '',
    website: '', languages: 'fr, ar',
  });
  const [hours, setHours] = useState<Record<string, BusinessHour>>({});
  const [socials, setSocials] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const p = await getBusinessProfile(org.id);
      setProfile(p);
      if (p) {
        setForm({
          name: p.name || '', description: p.description || '', shortDescription: p.short_description || '',
          category: p.category || '', logo: p.logo || '', cover: p.cover || '', address: p.address || '',
          city: p.city || '', country: p.country || '', phone: p.phone || '', whatsapp: p.whatsapp || '',
          email: p.email || '', website: p.website || '', languages: (p.languages || []).join(', '),
        });
        setHours(p.hours || {});
        setSocials(p.socials || {});
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const completeness = useMemo(() => profileCompleteness(profile), [profile]);
  const seo = useMemo(() => profile ? buildProfileSeo(profile) : null, [profile]);
  const levelMeta = verificationLevelMeta(profile?.verification_level);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      await upsertBusinessProfile({
        organizationId: org.id,
        tenantId: org.tenant_id,
        name: form.name || org.commercial_name || `Commerce ${org.id}`,
        description: form.description, shortDescription: form.shortDescription, category: form.category,
        logo: form.logo, cover: form.cover, address: form.address, city: form.city, country: form.country,
        phone: form.phone, whatsapp: form.whatsapp, email: form.email, website: form.website,
        hours, socials, languages: form.languages.split(',').map((s) => s.trim()).filter(Boolean),
      });
      setSaved(true);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const togglePublic = async () => {
    if (!profile) return;
    try {
      await setBusinessProfilePublic(org.id, !(profile.is_public));
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur.');
    }
  };

  if (loading) return <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>;

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Profil public</span> standardisé de votre entreprise. Il alimente ensuite
        <span className="font-medium text-foreground-600"> Zifek Space · la recherche · le SEO · Zifek AI · vos pages publiques</span>. Une seule fiche, tous les canaux.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4 sm:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-foreground-900">Complétion du profil</span>
            <span className="text-sm font-bold text-primary-600">{completeness.percent}%</span>
          </div>
          <div className="h-2 rounded-full bg-background-100 overflow-hidden">
            <div className="h-full bg-primary-500 transition-all" style={{ width: `${completeness.percent}%` }}></div>
          </div>
          {completeness.missing.length > 0 && (
            <p className="mt-2 text-[11px] text-foreground-400">À compléter : {completeness.missing.join(', ')}</p>
          )}
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <p className="text-xs text-foreground-500">Niveau de vérification</p>
          <span className={`inline-flex items-center gap-1.5 mt-1.5 px-3 py-1 rounded-full text-xs font-medium ${levelMeta.className}`}>
            <i className={levelMeta.icon}></i>{levelMeta.label}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 bg-background-50 border border-background-200/70 rounded-lg px-4 py-3">
        <div className="flex items-center gap-2">
          <i className={`${profile?.is_public ? 'ri-global-line text-primary-600' : 'ri-eye-off-line text-foreground-400'}`}></i>
          <span className="text-sm text-foreground-700">{profile?.is_public ? 'Profil public (visible dans Zifek Space)' : 'Profil privé'}</span>
        </div>
        <button onClick={togglePublic} className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${profile?.is_public ? 'bg-primary-500' : 'bg-background-200'}`}>
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 transition-all ${profile?.is_public ? 'left-[22px]' : 'left-0.5'}`}></span>
        </button>
      </div>

      <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-full overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${tab === t.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
            <i className={t.icon}></i>{t.label}
          </button>
        ))}
      </div>

      {tab === 'infos' && (
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Nom</span>
              <input className={INPUT} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Catégorie</span>
              <input className={INPUT} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ex : Restauration" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Logo (URL)</span>
              <input className={INPUT} value={form.logo} onChange={(e) => setForm({ ...form, logo: e.target.value })} />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Photo de couverture (URL)</span>
              <input className={INPUT} value={form.cover} onChange={(e) => setForm({ ...form, cover: e.target.value })} />
            </label>
          </div>
          <label className="block">
            <span className="block text-xs font-medium text-foreground-600 mb-1">Description courte</span>
            <input className={INPUT} value={form.shortDescription} onChange={(e) => setForm({ ...form, shortDescription: e.target.value })} maxLength={160} />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-foreground-600 mb-1">Description</span>
            <textarea className={`${INPUT} min-h-[90px]`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={1000}></textarea>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block sm:col-span-1">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Adresse</span>
              <input className={INPUT} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Ville</span>
              <input className={INPUT} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Pays</span>
              <input className={INPUT} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
            </label>
          </div>
        </div>
      )}

      {tab === 'hours' && (
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 space-y-2">
          {WEEK_DAYS.map((d) => {
            const h = hours[d.key] || { day: d.key, open: '09:00', close: '18:00', closed: false };
            return (
              <div key={d.key} className="flex items-center gap-3">
                <span className="w-24 text-sm text-foreground-700">{d.label}</span>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={!!h.closed} onChange={(e) => setHours({ ...hours, [d.key]: { ...h, closed: e.target.checked } })} className="w-4 h-4 accent-primary-500 cursor-pointer" />
                  <span className="text-xs text-foreground-500">Fermé</span>
                </label>
                {!h.closed && (
                  <>
                    <input type="time" className={`${INPUT} !w-32`} value={h.open || '09:00'} onChange={(e) => setHours({ ...hours, [d.key]: { ...h, open: e.target.value } })} />
                    <span className="text-foreground-400">→</span>
                    <input type="time" className={`${INPUT} !w-32`} value={h.close || '18:00'} onChange={(e) => setHours({ ...hours, [d.key]: { ...h, close: e.target.value } })} />
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === 'contact' && (
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Téléphone</span>
              <input className={INPUT} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">WhatsApp</span>
              <input className={INPUT} value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Email</span>
              <input className={INPUT} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Site web</span>
              <input className={INPUT} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" />
            </label>
            <label className="block sm:col-span-2">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Langues parlées (séparées par des virgules)</span>
              <input className={INPUT} value={form.languages} onChange={(e) => setForm({ ...form, languages: e.target.value })} />
            </label>
          </div>
          <div className="space-y-2">
            {SOCIAL_NETWORKS.map((s) => (
              <div key={s.key} className="flex items-center gap-3">
                <span className="w-28 flex items-center gap-2 text-sm text-foreground-700"><i className={s.icon}></i>{s.label}</span>
                <input className={INPUT} value={socials[s.key] || ''} onChange={(e) => setSocials({ ...socials, [s.key]: e.target.value })} placeholder={s.placeholder} />
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'seo' && seo && (
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 space-y-4">
          <div>
            <p className="text-xs font-semibold text-foreground-600 mb-1">Aperçu Google</p>
            <div className="rounded-md border border-background-200/70 p-3">
              <p className="text-[11px] text-foreground-400">{form.website || `zifek.space/${org.id}`}</p>
              <p className="text-base text-primary-700 truncate">{seo.title}</p>
              <p className="text-xs text-foreground-600">{seo.description}</p>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-foreground-600 mb-1">Mots-clés</p>
            <div className="flex flex-wrap gap-1.5">
              {seo.keywords.map((k) => <span key={k} className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[11px]">{k}</span>)}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-foreground-600 mb-1">Résumé pour Zifek AI</p>
            <p className="text-xs text-foreground-700 bg-background-100 rounded-md p-3">{profile ? buildProfileSummary(profile) : '—'}</p>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving} className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
          {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>} Enregistrer le profil
        </button>
        {saved && <span className="text-sm text-primary-700 flex items-center gap-1.5"><i className="ri-checkbox-circle-line"></i> Profil enregistré.</span>}
      </div>
    </div>
  );
}